import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {SelectionCriteriaVariant, useFanCriteria} from "./useFanCriteria";
import {useFetchAllObjs} from "../../platform/hooks/PlatformTypeScriptHooks";
import {PressureCorrector} from "../../business/ImpellerSimulationCriteria";
import {useFetchAllBladeProfiles} from "../profile/BladeProfileService";
import {Material} from "../../business/mechanical/Material";
import {Card} from "../../components/Card";
import useFanSelection, {FanSelectionMatchedFan, FanSelectionResult, matchedFanToSimulationCriteria} from "./SelectionService";
import UboldSpinner from "../../ubold-components/UboldSpinner";
import {SelectionMatchedFansChart} from "./prototype/SelectionMatchedFansChart";
import {AdjustableImpellerOverviewJson, BladeProfileReportJsonEntry, useFetchImpellerOverviewsReport} from "../impeller/AdjustableImpellerService";
import {SelectionRequestJson} from "../../business/ApiJsonTypes";
import {FanApplicationTypeJson} from "../../business/flow/FanApplicationTypes";
import {FanSelectionResultBrowser} from "./FanSelectionResultBrowser";
import {toFanSelectionMatches} from "./toFanSelectionMatches";
import {createInternationalizationPrefixer} from "../../platform/i18n/i18n";
import {useUserPreferences} from "../../contexts/UserPreferencesContext";
import AjaxService from "../../platform/ajax/AjaxService";
import {LATEST_OPTISEL_VERSION} from "../../components/Changelog";
import {isSuccessful, SimulatedDatasheetResponse, SuccessfulDatasheetResponse} from "../../business/useImpellerDatasheet";
import {FailedSimulationResponse} from "../../business/useImpellerSimulation";
import {useCurrentUserPrivileges} from "../../platform/hooks/PlatformHooks";
import {Protected, useHasCurrentUserPrivilege} from "../../layout/Protected";


export function FanSelectionPage() {
    const nav = useNavigate();
    const hasSelectionAccess = useHasCurrentUserPrivilege("PERM_SELECTION");
    const hasDevTools = useHasCurrentUserPrivilege("PERM_DEVELOPMENT_TOOLS");

    useEffect(() => {
        if (hasSelectionAccess === false) {
            nav("/");
        }
    }, [hasSelectionAccess]);

    // TODO Remove code duplication
    const availableInletShapes = useFetchAllObjs<PressureCorrector[] | null>("/api/inlet-shapes");
    const availableProfiles = useFetchAllBladeProfiles();
    const availableMaterials = useFetchAllObjs<Material[]>("/api/available-materials");
    const availableShaftDiameters: number[] | null = useFetchAllObjs<number[]>("/api/mechanical/available-shaft-diameters/all");
    const adjustableImpellerOverviewsOrNull = useFetchImpellerOverviewsReport();
    const applicationTypesForForcedDraft = useFetchAllObjs<FanApplicationTypeJson[]>("/api/application-types?operationalSetup=FORCED_DRAFT");
    const applicationTypesForInducedDraft = useFetchAllObjs<FanApplicationTypeJson[]>("/api/application-types?operationalSetup=INDUCED_DRAFT");

    const {allowUnlimitedCriteriaValues} = useUserPreferences();

    if (hasSelectionAccess !== true) {
        return <div className={"text-center mt-5"}><UboldSpinner/></div>;
    }

    if (availableInletShapes == null || availableProfiles === null || availableMaterials === null || adjustableImpellerOverviewsOrNull.result == null || availableShaftDiameters == null || applicationTypesForForcedDraft == null || applicationTypesForInducedDraft == null) {
        return <div className={"text-center mt-5"}><UboldSpinner/></div>;
    }

    const selectableProfiles = hasDevTools === true ? availableProfiles : availableProfiles.filter(profile => !profile.demonstrational);
    if (selectableProfiles.length === 0) {
        return <div className={"text-white text-center mt-5"}>No available blade profiles, contact technical support.</div>;
    }


    return <FanSelectionPageLoaded
        key={"remount-if-constraints-unlocked" + allowUnlimitedCriteriaValues}
        availableInletShapes={availableInletShapes}
        availableProfiles={availableProfiles}
        availableMaterials={availableMaterials}
        adjustableImpellerOverviews={adjustableImpellerOverviewsOrNull.result}
        availableShaftDiameters={availableShaftDiameters}
        applicationTypesForForcedDraft={applicationTypesForForcedDraft}
        applicationTypesForInducedDraft={applicationTypesForInducedDraft}/>;

}

const t = createInternationalizationPrefixer("Preselection");

export function FanSelectionPageLoaded({
                                           availableInletShapes,
                                           availableProfiles,
                                           availableMaterials,
                                           availableShaftDiameters,
                                           adjustableImpellerOverviews,
                                           applicationTypesForForcedDraft,
                                           applicationTypesForInducedDraft
                                       }: {
    availableInletShapes: PressureCorrector[],
    availableProfiles: BladeProfileReportJsonEntry[],
    availableMaterials: Material[],
    adjustableImpellerOverviews: AdjustableImpellerOverviewJson[],
    availableShaftDiameters: number[],
    applicationTypesForForcedDraft: FanApplicationTypeJson[],
    applicationTypesForInducedDraft: FanApplicationTypeJson[]
}) {


    const [showNotificationsLog, setShowNotificationsLog] = useState(false);

    const {
        selectionRequestJsonOrIncorrect,
        formElement
    } = useFanCriteria(availableInletShapes,
        availableProfiles,
        availableMaterials,
        availableShaftDiameters,
        adjustableImpellerOverviews,
        applicationTypesForForcedDraft,
        applicationTypesForInducedDraft,
        SelectionCriteriaVariant.GENERIC_FAN_SELECTION,
        null);

    const {
        results,
        loading
    } = useFanSelection(selectionRequestJsonOrIncorrect);

    const hasAccessToUntriagedSelection: boolean | "loading" = useHasCurrentUserPrivilege("PERM_SELECTION_UNTRIAGED_SELECTION")
    const hasAccessToFailedAssemblySelection: boolean | "loading" = useHasCurrentUserPrivilege("PERM_SELECTION_FAILED_ASSEMBLY_SELECTION")

    if (hasAccessToUntriagedSelection === "loading" || hasAccessToFailedAssemblySelection === "loading") {
        return <>...checking access...</>; // Just emergency placehodler, at this stage its practically sure privileges will be already loaded
    }

    // FAILED_ASSEMBLY to niższy wariant UNTRIAGED: też pomija pre-triaż i pokazuje pełną listę (w tym przeciążone
    // i bez złożenia), ale przycisk Triażu oraz labelki pozostają bramkowane UNTRIAGED, więc dla samego
    // FAILED_ASSEMBLY nie pojawią się.
    const showFullResultsWithoutTriage = hasAccessToUntriagedSelection === true || hasAccessToFailedAssemblySelection === true;

    return <>
        {formElement}
        {loading === true && <Card additionalClassNames={"mb-3 mt-3"}>
            <div className={" d-flex justify-content-center"}>
                <UboldSpinner/>
                <div style={{minHeight: "800px"}} data-role="improves experience on live search - removes jumping scroll"/>
            </div>
        </Card>}

        {showFullResultsWithoutTriage ? <FanSelectionResultsPresenter results={results} loading={loading}/> : <MechanicalTriagingProxyResultsPresenter
            key={results.criteriaUsed?.signalIdentifier ?? JSON.stringify(results.criteriaUsed)}
            results={results}
            loading={loading}/>}

        <Protected privilege={"PERM_SELECTION_CALCULATION_LOGS"}>
            <>
                {results.notifications.length > 0 && showNotificationsLog && <Card additionalClassNames={"mb-3 mt-3 text-gray-400"}>
                    {results.notifications.map((n, i) => <div key={i}>{n}</div>)}
                </Card>}

                {!showNotificationsLog && <>
                    <button className={"btn btn-outline btn-secondary"} onClick={() => setShowNotificationsLog(true)}>{t("Pokaż obliczenia")}</button>
                </>}
            </>
        </Protected>
    </>;
}

export function MechanicalTriagingProxyResultsPresenter({results, loading}: {
    results: FanSelectionResult,
    loading: boolean
}) {

    // Degroup typical ~200 results into just core one-per-fan-name to limit amout to triage
    const matchedFansByFirstFanNameOccurence = results.fanSelectionMatchedFans.filter((fan, index, self) =>
        index === self.findIndex(f => f.generalSimulationResult.parameterCatalog.fanName === fan.generalSimulationResult.parameterCatalog.fanName) && fan.selectionStrategyCode === "Optisel1"
    );

    const allIdentifiers = matchedFansByFirstFanNameOccurence.map(mf => mf.matchIdentifier)

    const [triagedIdentifiers, setTriagedIdentifiers] = useState<string[]>([]);
    const [nonOverloadedMatchedFanse, setNonOverloadedMatchedFans] = useState<FanSelectionMatchedFan[]>([]);


    useEffect(() => {
        const triageNextIdentifier = async () => {
            // Find first identifier not yet triaged
            const nextIdentifier = allIdentifiers.find(
                id => !triagedIdentifiers.includes(id)
            );

            // If no more identifiers to triage, exit
            if (!nextIdentifier) return;

            const simIdForDatasheet = "C(" + nextIdentifier + ")__simid_" + Math.round((Math.random() * 10000000));

            const triagedMatchedFan = matchedFansByFirstFanNameOccurence.filter(mf => mf.matchIdentifier == nextIdentifier)[0];
            const criteria = matchedFanToSimulationCriteria(results.criteriaUsed!, triagedMatchedFan);

            try {
                const newVar: SuccessfulDatasheetResponse | FailedSimulationResponse = await AjaxService.postJson(
                    "/api/datasheet-simulated?appVersion=" + LATEST_OPTISEL_VERSION,
                    criteria,
                    undefined,
                    simIdForDatasheet
                ).then(result => {
                    return result.json as SimulatedDatasheetResponse;
                });

                setTriagedIdentifiers(prev => [...prev, nextIdentifier]);

                if (!isSuccessful(newVar) ||
                    newVar.result.mechanicalResultDatasheetParameters === null) {
                    console.log("Triage rejected", newVar)
                    return;
                }

                const mechanicalParams = newVar.result.mechanicalResultDatasheetParameters;
                const mechanicalScore = mechanicalParams.mechanicalSelectionScore;
                const bpfResonating = mechanicalParams.resonanceReport.bpfResonating;

                if (!mechanicalParams.possibleOverload && !bpfResonating) {
                    console.log("passed triage", triagedMatchedFan.generalSimulationResult.parameterCatalog.fanName)
                    setNonOverloadedMatchedFans((prev) => [...prev, triagedMatchedFan])
                } else {
                    console.log("failed triage", triagedMatchedFan.generalSimulationResult.parameterCatalog.fanName)
                }
            } catch (e) {
                console.log("Triage error, skipping", nextIdentifier, e);
                setTriagedIdentifiers(prev => [...prev, nextIdentifier]);
            }

        };

        triageNextIdentifier();
    }, [triagedIdentifiers, JSON.stringify(allIdentifiers)]);

    if (allIdentifiers.length == triagedIdentifiers.length) {
        return <FanSelectionResultsPresenter results={{...results, fanSelectionMatchedFans: nonOverloadedMatchedFanse}} loading={loading}/>
    } else {
        return <div className={"pt-3"}>
            <Card className={"m-5"}>
                <div className={"text-center "}>
                    <div>
                        <UboldSpinner/>
                    </div>
                    <h2> {triagedIdentifiers.length} / {allIdentifiers.length}</h2>
                </div>
            </Card>
        </div>
    }
}


export function FanSelectionResultsPresenter({results, loading}: {
    results: FanSelectionResult,
    loading: boolean
}) {

    const [pickedMatchIdentifier, setPickedMatchIdentifier] = useState<string>("not-existing");

    return <>

        {!loading && results.criteriaUsed !== null &&
            <Card additionalClassNames={"mb-3 mt-3"} header={t("Wyniki selekcji - dobór klasyczny")}
                  subHeader={t("Dobór klasyczny w oparciu o zadaną średnicę i RPM, rozszerzony o warianty kątów łopatowych (posortowanych wg sumy błędów)")}>
                {results.fanSelectionMatchedFans.length > 0 && <>
                    <h5 className={"text-center m-0"}>{t("Charakterystyka przepływowa")}</h5>
                    <SelectionMatchedFansChart
                        pickedMatchIdentifier={pickedMatchIdentifier}
                        matchedFans={results.fanSelectionMatchedFans.filter(mf => mf.selectionStrategyCode === "Optisel1")}
                        requestedPoint={{
                            airFlow: results.criteriaUsed.fanWorkingPointQuery != null ? results.criteriaUsed.fanWorkingPointQuery.requestedFanAirFlow ?? 0.0 : results.criteriaUsed.installationWorkingPointQuery!.requestedInstallationAirFlow ?? 0.0,
                            pressure: results.criteriaUsed.fanWorkingPointQuery != null ? results.criteriaUsed.fanWorkingPointQuery.requestedFanStaticPressure ?? 0.0 : results.criteriaUsed.installationWorkingPointQuery!.requestedInstallationStaticPressure ?? 0.0
                        }}/>

                    <FanSelectionResultBrowser
                        pickedMatchIdentifier={pickedMatchIdentifier}
                        setPickedMatchIdentifier={setPickedMatchIdentifier}
                        matches={toFanSelectionMatches(results.fanSelectionMatchedFans.filter(mf => mf.selectionStrategyCode === "Optisel1"), results.criteriaUsed)}/>

                </>}
                {results.fanSelectionMatchedFans.length === 0 && <>
                    <div className={"text-center text-muted p-4 fs-3"}>Brak wyników</div>
                </>}
            </Card>}

        <Protected privilege={"PERM_SELECTION_EXTENDED_SELECTION"}>
            <>
                {!loading && results.criteriaUsed?.fanWorkingPointQuery == null && <Card header={t("Wyniki selekcji - dobór rozszerzony")}>
                    <div className={"text-center text-muted p-4 fs-3"}>{t("Dobór rozszerzony (automatyczna średnica i RPM) jest obecnie dostępny tylko w opcji \"Zadano ciśnienie samego wentylatora\", gdyż nie obsługuje korekt.")}</div>
                </Card>}
                {!loading && results.criteriaUsed !== null && results.criteriaUsed?.fanWorkingPointQuery != null && results.fanSelectionMatchedFans.length == 0 && <Card header={t("Wyniki selekcji - dobór rozszerzony")}>
                    <div className={"text-center text-muted p-4 fs-3"}>{t("Brak wyników")}</div>
                    {/* WARNIN THIS IS DUPLICATED ALSO IN FAN SELECTION FAN LIST */}
                </Card>}

                {results.fanSelectionMatchedFans.filter(mf => mf.selectionStrategyCode === "flexible").length > 0 && <>
                    {!loading && results.criteriaUsed !== null && results.criteriaUsed?.fanWorkingPointQuery != null && results.fanSelectionMatchedFans.length > 0 && <Card additionalClassNames={"mb-3"} header={t("Wyniki selekcji - dobór rozszerzony")}
                                                                                                                                                                            subHeader={t("Dobór rozszerzony, ignorujący zadane RPM i średnicę. Dobór opiera się o próbkowanie kilku(nastu) punktów charakterystyki każdej nieułamkowej opcji kątowej, ustawiając charakterystykę za pomocą średnicy i prędkości, by próbkowany punkt wypadał w zadanym punkcie pracy, a z uzyskanych nastawów wybierane są po 4 per jeden nastawialny wirnik - optymalne pod kątem średnicy, prędkości, sprawności i SPL. Analiza tylko całkowitych (nieułamkowych) opcji kątowych i tylko do kilkunastu punktów ch-ki dla tych kątów jest obecnie podytkowana wydajnością/czasem doboru.")}>
                        <h5 className={"text-center m-0"}>{t("Charakterystyka przepływowa")}</h5>
                        <SelectionMatchedFansChart
                            pickedMatchIdentifier={pickedMatchIdentifier}
                            matchedFans={results.fanSelectionMatchedFans.filter(mf => mf.selectionStrategyCode === "flexible")}
                            requestedPoint={{
                                airFlow: results.criteriaUsed.fanWorkingPointQuery != null ? results.criteriaUsed.fanWorkingPointQuery.requestedFanAirFlow ?? 0.0 : results.criteriaUsed.installationWorkingPointQuery!.requestedInstallationAirFlow ?? 0.0,
                                pressure: results.criteriaUsed.fanWorkingPointQuery != null ? results.criteriaUsed.fanWorkingPointQuery.requestedFanStaticPressure ?? 0.0 : results.criteriaUsed.installationWorkingPointQuery!.requestedInstallationStaticPressure ?? 0.0
                            }}/>
                        <div className={"text-muted text-center my-4"}>
                            <FanSelectionResultBrowser
                                pickedMatchIdentifier={pickedMatchIdentifier}
                                setPickedMatchIdentifier={setPickedMatchIdentifier}
                                matches={toFanSelectionMatches(results.fanSelectionMatchedFans.filter(mf => mf.selectionStrategyCode === "flexible"), results.criteriaUsed)}/>

                        </div>
                    </Card>}
                </>}
            </>
        </Protected>


    </>
}